import { useEffect, useState } from "react";
import { Check, ShieldCheck, Truck, BadgeCheck, Instagram, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { fetchProducts, imgSrc } from "@/lib/api";
import { waLink, IG_IDEALIZE } from "@/lib/brand";
import { MugOrderDialog } from "./MugOrderDialog";
import { CheckoutDialog } from "@/components/shop/CheckoutDialog";
import { PayInfo } from "@/components/shop/PayInfo";
import { Reveal } from "./Motion";

const CATS = ["Presentes", "Lembranças", "Empresas", "Eventos"];
const PERKS = [
  { icon: ShieldCheck, t: "Impressão de alta qualidade" },
  { icon: Truck, t: "Entrega rápida" },
  { icon: BadgeCheck, t: "Produtos de qualidade" },
];

const MugCard = ({ m, idx, onOrder, onBuy }) => (
  <Reveal delay={(idx % 2) * 0.1}>
    <motion.article data-testid={`idealize-mug-${idx}`} whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 240, damping: 22 }} className="group flex h-full flex-col rounded-[6px] border border-[#b08a3e]/25 bg-[#fbf7f2] p-4 sm:p-5">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[3px] bg-[#efe4da]">
        <img src={imgSrc(m.image)} alt={m.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-105" />
      </div>
      <div className="flex flex-1 flex-col pt-6 px-1">
        <p className="font-jost text-xs tracking-[0.3em] text-[#8a6a2c]">{m.description}</p>
        <h3 className="mt-2 font-serif-lux text-2xl sm:text-3xl font-semibold leading-tight text-[#1d1a17]">{m.name}</h3>
        <ul className="mt-4 space-y-1.5">
          {(m.features || []).map((f) => <li key={f} className="flex items-start gap-2 text-sm text-[#4a4440]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#b08a3e]" />{f}</li>)}
        </ul>
        <div className="mt-auto pt-6 flex items-center justify-between gap-3">
          <p data-testid={`idealize-mug-price-${idx}`} className="font-serif-lux text-3xl font-semibold text-[#1d1a17]">{m.price ? `R$ ${m.price}` : "Consulte"}</p>
          <div className="flex items-center gap-2">
            <button data-testid={`idealize-mug-wa-${idx}`} onClick={() => onOrder(m)} aria-label="Pedir pelo WhatsApp" className="grid h-10 w-10 place-items-center rounded-full border border-[#1d1a17]/20 text-[#1d1a17] transition-colors hover:bg-white"><MessageCircle className="h-4 w-4" /></button>
            {m.price ? <button data-testid={`idealize-mug-cta-${idx}`} onClick={() => onBuy(m)} className="rounded-full bg-[#1d1a17] px-5 py-2.5 font-jost text-xs tracking-[0.2em] text-[#f7f0ea] transition-colors duration-300 hover:bg-[#8a6a2c]">COMPRAR</button> : null}
          </div>
        </div>
      </div>
    </motion.article>
  </Reveal>
);

export const Idealize = () => {
  const [mugs, setMugs] = useState([]);
  const [ordering, setOrdering] = useState(null);
  const [buying, setBuying] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchProducts("idealize").then((d) => { if (alive) setMugs(d); });
    return () => { alive = false; };
  }, []);

  return (
    <section id="idealize" data-testid="idealize-section" className="relative bg-[#f8efe8] text-[#1d1a17]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10 py-24 sm:py-36 pt-32 sm:pt-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <img src="/brand/idealize.jpg" alt="Idealize Personalizados" className="-ml-4 h-36 w-36 sm:h-44 sm:w-44 object-cover" />
              <p className="mt-6 font-jost text-xs tracking-[0.4em] text-[#8a6a2c]">IDEALIZE PERSONALIZADOS</p>
              <h2 className="mt-4 font-serif-lux text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95]">Canecas<br /><em className="text-[#8a6a2c]">Express</em></h2>
              <p className="mt-6 font-serif-lux italic text-2xl sm:text-3xl text-[#4a4440] leading-snug">Mais que canecas, são histórias em cada detalhe.</p>
              <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 font-jost text-xs tracking-[0.3em] text-[#4a4440]">
                {CATS.map((c, i) => <li key={c} className="flex items-center gap-5">{c.toUpperCase()}{i < CATS.length - 1 && <span className="h-3 w-px bg-[#b08a3e]/50" />}</li>)}
              </ul>
              <div className="mt-10 flex flex-wrap gap-3">
                <a data-testid="idealize-whatsapp-btn" href={waLink("Olá, Idealize! Quero fazer uma caneca personalizada.")} target="_blank" rel="noreferrer" className="rounded-full bg-[#8a6a2c] px-7 py-4 font-jost text-xs tracking-[0.25em] text-white transition-transform duration-300 hover:-translate-y-0.5">PERSONALIZAR AGORA</a>
                <a data-testid="idealize-instagram-link" href={IG_IDEALIZE.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#1d1a17]/20 px-6 py-4 font-jost text-xs tracking-[0.2em] transition-colors hover:bg-[#1d1a17] hover:text-[#f7f0ea]"><Instagram className="h-4 w-4" />{IG_IDEALIZE.handle}</a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {mugs.length === 0 ? (
              <p data-testid="idealize-empty" className="text-[#4a4440]">Catálogo em atualização. Chame no WhatsApp.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {mugs.map((m, i) => <MugCard key={m.id || i} m={m} idx={i} onOrder={setOrdering} onBuy={setBuying} />)}
              </div>
            )}
            <Reveal className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-[#b08a3e]/30 pt-8">
              {PERKS.map(({ icon: I, t }) => <div key={t} className="flex items-center gap-3 font-jost text-xs tracking-[0.18em] text-[#4a4440]"><I className="h-6 w-6 text-[#b08a3e]" strokeWidth={1.4} />{t.toUpperCase()}</div>)}
            </Reveal>
            <Reveal className="mt-6"><PayInfo light /></Reveal>
          </div>
        </div>
      </div>
      {ordering && <MugOrderDialog mug={ordering} onClose={() => setOrdering(null)} />}
      {buying && <CheckoutDialog product={buying} light customize onClose={() => setBuying(null)} />}
    </section>
  );
};
